'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ATTRIBUTION_LOCAL_STORAGE_KEY,
  type AttributionData,
  getAttributionData as getAttributionDataFromLocalStorage,
  setAttributionData as setAttributionDataInLocalStorage,
} from 'utils/attribution-helpers'
import { logger } from 'utils/logger'

interface IAttributionContext {
  attribution: AttributionData
  updateAttribution: (data: AttributionData) => void
}

const AttributionContext = createContext<IAttributionContext>({
  attribution: {},
  updateAttribution: () => {},
})

/**
 * Manages attribution data (such as UTM parameters) for the application, and
 * provides up-to-date attribution information via React context and in
 * localStorage.
 */
export const AttributionProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [attributionDataInComponentState, setAttributionDataInComponentState] =
    useState<AttributionData>({})

  /**
   * Updates the attribution data both in component state and in local storage.
   */
  const updateAttribution = useCallback((data: AttributionData) => {
    setAttributionDataInComponentState(data)
    try {
      setAttributionDataInLocalStorage(data)
    } catch (error) {
      logger.error('Failed to update attribution data:', error)
    }
  }, [])

  /**
   * On mount (and never again), if attribution data is available from URL query
   * params, use it to update the component state and persist it to
   * localStorage. If no attribution data is available from URL query params,
   * read the persisted attribution data from localStorage and use it to update
   * the component state.
   */
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search)
    const utmSource = urlSearchParams.get('utm_source') ?? undefined
    const utmMedium = urlSearchParams.get('utm_medium') ?? undefined

    if (utmSource || utmMedium) {
      const newData: AttributionData = {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: urlSearchParams.get('utm_campaign') ?? undefined,
        ad_group_id: urlSearchParams.get('ad_group_id') ?? undefined,
        kw: urlSearchParams.get('kw') ?? undefined,
        network: urlSearchParams.get('network') ?? undefined,
        placement: urlSearchParams.get('placement') ?? undefined,
        utm_content: urlSearchParams.get('utm_content') ?? undefined,
        search: window.location.search || undefined,
      }
      updateAttribution(newData)
    } else {
      try {
        const stored = getAttributionDataFromLocalStorage()
        setAttributionDataInComponentState(stored)
      } catch (error) {
        logger.error('Failed to read persisted attribution data:', error)
      }
    }
  }, [updateAttribution])

  /**
   * Listen for changes to the attribution data in local storage to ensure
   * that all tabs/windows of the app stay in sync with the latest attribution
   * data, even if they were opened before the latest attribution data was set
   */
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key === ATTRIBUTION_LOCAL_STORAGE_KEY) {
        if (event.newValue) {
          try {
            setAttributionDataInComponentState(JSON.parse(event.newValue))
          } catch (error) {
            logger.error(
              'Failed to parse attribution data from storage:',
              error,
            )
          }
        } else {
          setAttributionDataInComponentState({})
        }
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const value = useMemo(
    () => ({ attribution: attributionDataInComponentState, updateAttribution }),
    [attributionDataInComponentState, updateAttribution],
  )

  return (
    <AttributionContext.Provider value={value}>
      {children}
    </AttributionContext.Provider>
  )
}

export const useAttribution = (): IAttributionContext => {
  return useContext(AttributionContext)
}
